import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2,
  ArrowLeft, ArrowRight, X,
} from "lucide-react";
import { api } from "@/lib/api";
import IntelligenceHub from "./IntelligenceHub";

const IGNORE = "__ignore__";

const DEFAULT_TARGET_TYPES = [
  { value: "prospect",            label: "Prospect" },
  { value: "partner",             label: "Partenaire" },
  { value: "accountant",          label: "Comptable" },
  { value: "real_estate_broker",  label: "Courtier immobilier" },
  { value: "lawyer",              label: "Avocat" },
  { value: "notary",              label: "Notaire" },
  { value: "recruiter",           label: "Recruteur" },
  { value: "organization",        label: "Organisation" },
  { value: "employer",            label: "Employeur" },
  { value: "education",           label: "Éducation" },
  { value: "chamber",             label: "Chambre de commerce" },
  { value: "association",         label: "Association" },
  { value: "event",               label: "Événement" },
  { value: "influencer",          label: "Influenceur" },
  { value: "other",               label: "Autre" },
];

export default function TargetImport() {
  const navigate = useNavigate();
  const [step, setStep] = useState("upload"); // upload · mapping · result
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState({
    target_type: "prospect",
    share_scope: "private",
    source_du_contact: "Import CSV",
  });
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onSelectFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cdl/targets/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      setStep("mapping");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de lire ce fichier.");
    } finally {
      setUploading(false);
    }
  };

  const requiredMissing = useMemo(() => {
    if (!preview) return [];
    const mappedFieldKeys = new Set(Object.values(mapping).filter((v) => v && v !== IGNORE));
    return preview.fields.filter((f) => f.required && !mappedFieldKeys.has(f.key));
  }, [mapping, preview]);

  const commit = async () => {
    if (requiredMissing.length > 0) {
      toast.error(`Colonnes obligatoires manquantes : ${requiredMissing.map((f) => f.label_fr).join(", ")}`);
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post("/cdl/targets/import/commit", {
        rows: preview.all_rows || preview.sample_rows,
        mapping,
        defaults,
      });
      setResult(data);
      setStep("result");
      toast.success(`${data.created} cibles importées.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import impossible.");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <IntelligenceHub activeTab="import">
      <div className="max-w-5xl mx-auto" data-testid="intel-import-page">
        {step === "upload" && (
          <UploadStep
            uploading={uploading}
            fileRef={fileRef}
            onSelectFile={onSelectFile}
          />
        )}

        {step === "mapping" && preview && (
          <MappingStep
            preview={preview}
            mapping={mapping}
            setMapping={setMapping}
            defaults={defaults}
            setDefaults={setDefaults}
            requiredMissing={requiredMissing}
            onBack={() => setStep("upload")}
            onCommit={commit}
            committing={committing}
          />
        )}

        {step === "result" && result && (
          <ResultStep
            result={result}
            onNew={() => { setStep("upload"); setPreview(null); setResult(null); }}
            onGoList={() => navigate("/app/commercial/list")}
          />
        )}
      </div>
    </IntelligenceHub>
  );
}


function UploadStep({ uploading, fileRef, onSelectFile }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-[#C5A880]/60 bg-white/70 p-10 text-center"
         data-testid="intel-import-upload">
      <div className="mx-auto h-14 w-14 rounded-full bg-[#FBF6EA] flex items-center justify-center">
        <FileSpreadsheet className="h-7 w-7 text-[#7A5B14]" strokeWidth={1.5} />
      </div>
      <h3 className="mt-5 font-display text-2xl text-[#1F2937] tracking-tight">
        Importer une liste de cibles
      </h3>
      <p className="mt-2 text-sm text-[#5B5B62] max-w-xl mx-auto leading-relaxed">
        Chargez un fichier CSV ou Excel (.xlsx). L&apos;IA proposera automatiquement une correspondance
        entre vos colonnes et les champs ALTIORA. Maximum <strong>500 lignes</strong> par import.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        data-testid="intel-import-file-input"
        onChange={(e) => onSelectFile(e.target.files?.[0])}
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid="intel-import-file-button"
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-medium transition-colors"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                   : <Upload className="h-3.5 w-3.5" strokeWidth={2} />}
        {uploading ? "Analyse du fichier…" : "Choisir un fichier"}
      </button>

      <div className="mt-8 flex justify-center gap-6 text-xs text-[#5B5B62]">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> CSV, Excel .xlsx
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> Auto-mapping intelligent
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> Doublons ignorés
        </div>
      </div>
    </div>
  );
}


function MappingStep({ preview, mapping, setMapping, defaults, setDefaults, requiredMissing, onBack, onCommit, committing }) {
  return (
    <div className="space-y-6" data-testid="intel-import-mapping">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Étape 2 / 3</div>
          <h3 className="mt-1 font-display text-2xl text-[#1F2937] tracking-tight">
            Associer vos colonnes aux champs ALTIORA
          </h3>
          <p className="mt-1 text-sm text-[#5B5B62]">
            {preview.total_rows} lignes détectées · {preview.headers.length} colonnes
          </p>
        </div>
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#5B5B62] hover:text-[#0B2545]">
          <ArrowLeft className="h-3.5 w-3.5" /> Changer de fichier
        </button>
      </div>

      {/* Import defaults */}
      <div className="rounded-lg border border-[#E4E1D8] bg-[#F8F7F3]/60 p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">
            Type par défaut
          </label>
          <select
            value={defaults.target_type}
            onChange={(e) => setDefaults((d) => ({ ...d, target_type: e.target.value }))}
            data-testid="intel-import-default-type"
            className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
          >
            {DEFAULT_TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">
            Portée
          </label>
          <select
            value={defaults.share_scope}
            onChange={(e) => setDefaults((d) => ({ ...d, share_scope: e.target.value }))}
            className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
          >
            <option value="private">Privée (seulement moi)</option>
            <option value="agency">Agence</option>
            <option value="org">Organisation</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">
            Source du contact
          </label>
          <input
            type="text"
            value={defaults.source_du_contact}
            onChange={(e) => setDefaults((d) => ({ ...d, source_du_contact: e.target.value }))}
            className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Mapping table */}
      <div className="rounded-lg border border-[#E4E1D8] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F7F3] text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
            <tr>
              <th className="text-left px-4 py-3 w-1/3">Colonne du fichier</th>
              <th className="text-left px-4 py-3 w-1/3">→ Champ ALTIORA</th>
              <th className="text-left px-4 py-3">Aperçu</th>
            </tr>
          </thead>
          <tbody>
            {preview.headers.map((h, i) => (
              <tr key={h + i} className="border-t border-[#E4E1D8]">
                <td className="px-4 py-2.5 font-medium text-[#1F2937] truncate max-w-[200px]" title={h}>{h}</td>
                <td className="px-4 py-2.5">
                  <select
                    value={mapping[h] || IGNORE}
                    onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                    data-testid={`intel-import-map-${i}`}
                    className="w-full rounded-md border border-[#E4E1D8] bg-white px-2.5 py-1.5 text-xs"
                  >
                    <option value={IGNORE}>— Ignorer cette colonne —</option>
                    {preview.fields.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label_fr}{f.required ? " *" : ""}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2.5 text-xs text-[#5B5B62] truncate max-w-[300px]" title={preview.sample_rows[0]?.[h] || ""}>
                  {preview.sample_rows[0]?.[h] || <span className="italic opacity-60">vide</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {requiredMissing.length > 0 && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-2 text-sm text-red-900">
          <AlertTriangle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
          <div>
            Champs obligatoires non mappés : {requiredMissing.map((f) => f.label_fr).join(", ")}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onCommit}
          disabled={committing || requiredMissing.length > 0}
          data-testid="intel-import-commit"
          className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-medium transition-colors"
        >
          {committing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
          {committing ? `Import de ${preview.total_rows} lignes…` : `Importer ${preview.total_rows} lignes`}
        </button>
      </div>
    </div>
  );
}


function ResultStep({ result, onNew, onGoList }) {
  return (
    <div className="rounded-2xl border-2 border-emerald-300 bg-white p-8 md:p-10 text-center" data-testid="intel-import-result">
      <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="h-7 w-7 text-emerald-700" strokeWidth={1.8} />
      </div>
      <h3 className="mt-5 font-display text-2xl text-[#1F2937] tracking-tight">Import terminé</h3>
      <div className="mt-4 grid grid-cols-3 gap-4 max-w-xl mx-auto">
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800">Créées</div>
          <div className="mt-1 font-display text-3xl text-emerald-900 tabular-nums" data-testid="intel-import-result-created">
            {result.created}
          </div>
        </div>
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-800">Doublons ignorés</div>
          <div className="mt-1 font-display text-3xl text-amber-900 tabular-nums">
            {result.skipped_duplicates}
          </div>
        </div>
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-red-800">Erreurs</div>
          <div className="mt-1 font-display text-3xl text-red-900 tabular-nums">
            {result.errors}
          </div>
        </div>
      </div>

      {result.duplicates_sample?.length > 0 && (
        <div className="mt-6 text-left max-w-xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-2">Aperçu des doublons</div>
          <ul className="text-sm text-[#5B5B62] space-y-1">
            {result.duplicates_sample.map((d, i) => (
              <li key={i} className="flex items-center gap-2">
                <X className="h-3 w-3 opacity-50" /> Ligne {d.row} — {d.display_name}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 flex justify-center gap-3">
        <button onClick={onNew} className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] px-4 py-2 text-sm">
          Importer un autre fichier
        </button>
        <button onClick={onGoList} data-testid="intel-import-goto-list"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm">
          Voir la liste <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
